<template>
  <div :class="classes">
    <input
      v-if="isOpen"
      ref="inputEl"
      v-model="inputValue"
      type="number"
      :step="1"
      :min="0"
      :max="999"
      @blur="onBlur"
      @keydown.enter.prevent="onTabOrEnter"
      @keydown.tab.prevent="onTabOrEnter"
    >
    <div v-else class="closed-input" @click="onClick">
      <div class="t-lg">{{ inputValue }}</div>
      <donut-upgrade-tag v-if="getters.upgradeRequired(paywall)" class="m-lx1" />
    </div>
    <Transition name="status-icon">
      <div v-if="statusIcon" class="status-icon">
        <i v-if="statusIcon === 'loading'" class="mi anim-spin purple">refresh</i>
        <i v-if="statusIcon === 'success'" class="mi green">check</i>
        <i v-if="statusIcon === 'error'" class="mi red">close</i>
      </div>
    </Transition>
  </div>
</template>

<script setup>
  import { ref, computed, nextTick, watch } from 'vue';
  import { useStore } from 'vuex';

  const { dispatch, getters } = useStore();

  const props = defineProps({
    modelValue: { type: Number, required: true },
    paywall: { type: Object, default: null },
    disabled: { type: Boolean, default: false },
  });
  const emit = defineEmits(['submit', 'advance']);

  // Input
  const inputEl = ref(null);
  const inputValue = ref(props.modelValue);
  const isOpen = ref(false);
  const isLoading = ref(false);
  const statusIcon = ref(null);

  // Set Status Icon
  const setIcon = ({ icon }) => {
    statusIcon.value = icon;
    setTimeout(() => {
      isLoading.value = false;
      statusIcon.value = null;
    }, 500);
  };
  const onComplete = (args) => {
    if (Object.keys(args).includes('icon')) setIcon({ icon: args.icon });
    if (Object.keys(args).includes('value')) inputValue.value = args.value;
  };

  // Submission
  const canSubmit = computed(() => {
    // Don't submit if nothing is changed.
    if (props.modelValue === inputValue.value) return false;
    // Don't submit if the value is nil. This could conceviably allow nil values at some point via a bool prop. Currently, it does not.
    if (!inputValue.value && inputValue.value !== 0) return false;
    return true;
  });
  const submit = () => {
    isOpen.value = false;
    if (canSubmit.value) {
      // If they are equal, no need to do anything. Only when it's been changed should we run anything.
      isLoading.value = true;
      statusIcon.value = 'loading';
      emit('submit', inputValue.value, onComplete);
    } else {
      inputValue.value = props.modelValue;
    }
  };

  // Focusing
  const open = () => {
    if (!isLoading.value) {
      isOpen.value = true;
      nextTick(() => {
        // Wait for the inputEl to render once isOpen is true
        inputEl.value.focus();
        // If the value is changed from a different location, we want to reflect that newer value when we open up the input
        inputValue.value = props.modelValue;
      });
    }
  };
  defineExpose({ open }); // Allow the parent to access this so that it can focus on the next input when we emit('advance')

  // Interactions
  const onClick = () => {
    if (props.disabled) return;
    dispatch('openUpgradeModalIfRequired', props.paywall).then((upgradeIsRequired) => {
      if (!upgradeIsRequired) {
        open();
      }
    });
  };
  const onBlur = () => {
    if (props.disabled) return;
    submit();
  };
  const onTabOrEnter = () => {
    // Don't call submit directly here, because doing so will set isOpen to false, thus triggering
    // onBlur which will call submit again. Instead, just let these keypresses blur the input and
    // let onBlur handle that. emit('advance') tells the parent table to focus on the next row's component.
    if (props.disabled) return;
    inputEl.value.blur();
    emit('advance');
  };

  // Styling
  const classes = computed(() => {
    const list = ['async-number', 'f-ac'];
    if (isLoading.value || props.disabled) {
      list.push('pe-n');
      list.push('o-50');
    }
    return list;
  });

  watch(
    () => props.modelValue,
    (newValue) => {
      inputValue.value = newValue;
    },
  );
</script>
